import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal, Text, BlockStack } from '@shopify/polaris';
import { authenticatedFetch, useAuth } from './AuthComponent';
import { useStore } from './StoreContext';

import { hasRole } from './constants/roles';
function invClass(qty) {
    if (qty === 0) return 'badge badge--inv-empty';
    if (qty <= 3)  return 'badge badge--inv-warn';
    return 'badge badge--inv-ok';
}

function InvCell({ value }) {
    return (
        <td className="td-center">
            <span className={invClass(value)}>{value}</span>
        </td>
    );
}

function OnHandEditCell({ variant, onRequest }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft]     = useState(String(variant.on_hand));
    const [flash, setFlash]     = useState(false);

    useEffect(() => { setDraft(String(variant.on_hand)); }, [variant.on_hand]);

    const tryPropose = (next) => {
        if (!Number.isFinite(next) || next < variant.committed) {
            setFlash(true);
            setTimeout(() => setFlash(false), 350);
            return;
        }
        if (next === variant.on_hand) return;
        onRequest(variant, next);
    };

    const bump = (delta) => tryPropose(variant.on_hand + delta);

    const commitTyped = () => {
        const n = parseInt(draft, 10);
        setEditing(false);
        if (!Number.isFinite(n)) { setDraft(String(variant.on_hand)); return; }
        tryPropose(n);
        setDraft(String(n));
    };

    return (
        <td className="td-center">
            <div className={`inv-stepper${flash ? ' inv-stepper--flash' : ''}`}>
                <button type="button" className="inv-stepper__btn"
                        onClick={() => bump(-1)}
                        disabled={variant.on_hand <= variant.committed}
                        aria-label="decrease">−</button>
                {editing ? (
                    <input
                        type="number"
                        inputMode="numeric"
                        className="inv-stepper__input"
                        value={draft}
                        autoFocus
                        min={variant.committed}
                        onChange={e => setDraft(e.target.value)}
                        onBlur={commitTyped}
                        onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); commitTyped(); }
                            if (e.key === 'Escape') { setDraft(String(variant.on_hand)); setEditing(false); }
                        }}
                    />
                ) : (
                    <button type="button" className={`inv-stepper__value ${invClass(variant.on_hand)}`}
                            onClick={() => setEditing(true)}>
                        {variant.on_hand}
                    </button>
                )}
                <button type="button" className="inv-stepper__btn"
                        onClick={() => bump(1)}
                        aria-label="increase">+</button>
            </div>
        </td>
    );
}

export default function ProductDetail() {
    const { productId } = useParams();
    const navigate      = useNavigate();
    const { activeStore } = useStore();
    const { user }      = useAuth();
    const [product, setProduct]   = useState(null);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);
    const [activeImg, setActiveImg] = useState(null);
    const [pending, setPending]   = useState(null);
    const [saving, setSaving]     = useState(false);
    const [saveErr, setSaveErr]   = useState('');

    const canEditOnHand = hasRole(user, ['franchise']);

    const refetch = () => {
        // URL carries the bare numeric id (2026-10-09); the full GID still works.
        const raw = decodeURIComponent(productId);
        const gid = /^\d+$/.test(raw) ? `gid://shopify/Product/${raw}` : raw;
        return authenticatedFetch(`/api/products/${encodeURIComponent(gid)}`)
            .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
            .then(data => { setProduct(data); return data; });
    };

    useEffect(() => {
        setLoading(true);
        setError(null);
        refetch()
            .then(data => { setActiveImg(data.image || (data.images?.[0] ?? null)); })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

    const commitChange = async () => {
        if (!pending) return;
        setSaving(true); setSaveErr('');
        try {
            const r = await authenticatedFetch('/api/products/inventory/on-hand', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    inventory_item_id: pending.variant.inventory_item_id,
                    new_on_hand:       pending.next,
                    compare_on_hand:   pending.variant.on_hand,
                }),
            });
            if (!r.ok) {
                const msg = (await r.json().catch(() => null))?.detail || `HTTP ${r.status}`;
                throw new Error(msg);
            }
            await refetch();
            setPending(null);
        } catch (e) {
            setSaveErr(String(e.message || e));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="empty-state">Loading product&hellip;</div>;
    if (error)   return <div className="error-msg--banner error-msg">Error: {error}</div>;
    if (!product) return null;

    const images = product.images?.length ? product.images : (product.image ? [product.image] : []);

    return (
        <div>
            {/* Back link */}
            {/* Inventory remembers where this product was in the list and scrolls back to it. */}
            <button onClick={() => navigate(`/store/${activeStore}/products/inventory`)} className="btn-link btn-link--back">
                &lsaquo; Back to Products
            </button>

            {/* Title + location */}
            <div className="product-detail-header">
                <h1 className="page-title">{product.title}</h1>
                {product.location?.location_name && (
                    <div className="page-subtitle">{product.location.location_name}</div>
                )}
            </div>

            {/* Two-column layout */}
            <div className="product-detail-grid">
                {/* Left: images */}
                <div>
                    <div className="product-detail-main-img">
                        {activeImg
                            ? <img src={activeImg} alt="" className="product-detail-img" />
                            : <span className="products-img-placeholder">&#128230;</span>}
                    </div>
                    {images.length > 1 && (
                        <div className="product-detail-thumbs">
                            {images.map((url, i) => (
                                <div key={i} onClick={() => setActiveImg(url)}
                                    className="product-detail-thumb"
                                    style={{ borderColor: activeImg === url ? 'var(--color-primary)' : 'var(--color-border)' }}>
                                    <img src={url} alt="" className="product-detail-thumb-img" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Right: description + inventory */}
                <div>
                    {/* Description */}
                    {product.descriptionHtml && (
                        <div className="card product-detail-desc">
                            <div className="card__header">Description</div>
                            <div className="card__body product-detail-desc__body"
                                dangerouslySetInnerHTML={{ __html: product.descriptionHtml }} />
                        </div>
                    )}

                    {/* Inventory table */}
                    <div className="card card--no-pad">
                        <div className="card__header">Inventory</div>
                        <div className="table-scroll">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>SKU</th>
                                        <th>Variant</th>
                                        <th className="col-center">Committed</th>
                                        <th className="col-center">Available</th>
                                        <th className="col-center">On Hand</th>
                                        <th className="col-center">Incoming</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {product.variants.map((v) => (
                                        <tr key={v.id}>
                                            <td className="col-mono td-nowrap">{v.sku || '\u2014'}</td>
                                            <td className="td-variant">{v.title === 'Default Title' ? '\u2014' : v.title}</td>
                                            <InvCell value={v.committed} />
                                            <InvCell value={v.available} />
                                            {canEditOnHand && v.inventory_item_id
                                                ? <OnHandEditCell variant={v}
                                                                  onRequest={(variant, next) => { setSaveErr(''); setPending({ variant, next }); }} />
                                                : <InvCell value={v.on_hand} />}
                                            <InvCell value={v.incoming} />
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {pending && (
                <Modal
                    open
                    onClose={() => { if (!saving) { setPending(null); setSaveErr(''); } }}
                    title="Confirm inventory change"
                    primaryAction={{
                        content: saving ? 'Saving…' : 'Confirm',
                        onAction: commitChange,
                        loading: saving,
                        disabled: saving,
                    }}
                    secondaryActions={[
                        { content: 'Cancel', onAction: () => { setPending(null); setSaveErr(''); }, disabled: saving },
                    ]}
                >
                    <Modal.Section>
                        <BlockStack gap="200">
                            <Text variant="bodyMd">
                                Adjust <strong>{pending.variant.on_hand}</strong> → <strong>{pending.next}</strong> of
                                {' '}<span style={{ fontFamily: 'monospace' }}>{pending.variant.sku || pending.variant.title}</span>?
                            </Text>
                            {pending.variant.committed > 0 && (
                                <Text variant="bodySm" tone="subdued">
                                    {pending.variant.committed} committed to open orders (cannot drop below).
                                </Text>
                            )}
                            {saveErr && (
                                <Text variant="bodySm" tone="critical">{saveErr}</Text>
                            )}
                        </BlockStack>
                    </Modal.Section>
                </Modal>
            )}
        </div>
    );
}
